import { useEffect, useMemo, useState } from "react";
import { TABLE_ACTIONS } from "helpers/constants";
import { AcademicYear } from "types/academicYear";
import {
  useDeleteAcademicYearMutation,
  useGetAcademicYearsByOrgLazyQuery,
  useGetAcademicYearsTemplateQuery,
} from "graphql/__generated__/hooks";
import { useNavigate } from "react-router-dom";
import { useProgramOrganization } from "hooks/useProgramOrganization";

export const useYearlyCurriculumList = () => {
  const { organizationId } = useProgramOrganization();
  const navigate = useNavigate();
  const [deleteAcademicYearModal, setDeleteAcademicYearModal] = useState(false);
  const [academicYearIdToDelete, setAcademicYearIdToDelete] = useState("");

  const [deleteAcademicYear] = useDeleteAcademicYearMutation();
  const [getAcademicYearsQuery, { data, refetch }] =
    useGetAcademicYearsByOrgLazyQuery();
  const { data: dataTemplate } = useGetAcademicYearsTemplateQuery();

  const templates = useMemo(() => {
    return (
      dataTemplate?.academic_year.map((ay) => ({
        ...ay,
        tags: ay.tags?.map((t) => t.tag),
        requirements: ay.requirements.map((r) => r.requirement),
      })) || []
    );
  }, [dataTemplate]);

  const academicYears = useMemo(() => {
    return (
      (data?.program
        .map((p) => p.academic_years)
        .flat()
        .map((ay) => ({
          ...ay,
          tags: ay.tags?.map((t) => t.tag),
          requirements: ay.requirements.map((r) => r.requirement),
        })) as AcademicYear[]) || []
    );
  }, [data]);

  useEffect(() => {
    if (organizationId) {
      getAcademicYearsQuery({ variables: { orgId: organizationId } });
    }
  }, [organizationId]);

  const handleActionClick = (action: TABLE_ACTIONS, id: string) => {
    switch (action) {
      case TABLE_ACTIONS.VIEW:
        navigate(`/organization/${organizationId}/yearly-curriculum/${id}`);
        break;
      case TABLE_ACTIONS.EDIT:
        navigate(
          `/organization/${organizationId}/yearly-curriculum/${id}/edit/`,
        );
        break;
      case TABLE_ACTIONS.DELETE:
        setAcademicYearIdToDelete(id);
        setDeleteAcademicYearModal(true);
        break;
      default:
        break;
    }
  };

  const handleDelete = async () => {
    if (!academicYearIdToDelete || academicYearIdToDelete === "") {
      return;
    }
    await deleteAcademicYear({ variables: { id: academicYearIdToDelete } });
    await refetch();
    setAcademicYearIdToDelete("");
  };

  return {
    deleteAcademicYearModal,
    setDeleteAcademicYearModal,
    academicYears,
    handleActionClick,
    handleDelete,
    templates,
    organizationId,
  };
};
